import React from "react";
import axios from "axios";
import { Table, Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import GLOBALVAR from "../constants";
import { format } from 'date-fns';
import LoadingSpinner from "../components/misc/LoadingSpinner";
import jsPDF from "jspdf";
import 'jspdf-autotable';
import './css/ReportsStyles.scss'; 
import ToPeso from "../components/misc/ToPeso";
import logo from "../logo.png";
import calculateDueDates from "../components/misc/calculateDueDates";
import calculatePayments from "../components/misc/calculatePayments";

function ReportOutstandingBalanceByAccountOfficer() {
    const [paymentHeaders, setPaymentHeaders] = useState([]);
    const [officerLoans, setOfficerLoans] = useState({});
    const [showSpinner, setShowSpinner] = useState(false);
    const [endDate, setEndDate] = useState('');
    const [officers, setOfficers] = useState(null);

    useEffect(() => {
        if (!officers) {
            fetchOfficers();
        }
    }); // Fetch officers once when the component mounts

    const fetchOfficers = async () => {
        try {
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all/officers`, {
                params: {
                    endDate: endDate || format(new Date(), 'yyyy-MM-dd')
                }
            });
            setOfficers(response.data.rows);
        } catch (error) {
            console.error('Error fetching officers:', error);
        }
    };

    const fetchLoans = async (endDate, officer) => {
        setShowSpinner(true);

        try {
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all/outstanding/byofficer`, {
                params: {
                    endDate: endDate || format(new Date(), 'yyyy-MM-dd'),
                    officer: officer
                }
            });

            const loansData = response.data.rows.reduce((acc, item) => {
                const fullName = `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}`;
                const fullNameWithLink = (
                    <a href={`/applicant/${item.applicant.id}`}>
                        {fullName}
                    </a>
                );

                const loansnum = item.id.toString().padStart(6, '0');
                const loansNumWithLink = (
                    <a href={`/loans/${item.id}`}>
                        {loansnum}
                    </a>
                );

                const dates = calculateDueDates(item.dateGranted, item.loanInterval, item.loanTerm, item.amountGranted, item.interestRate, item.advanceInterest);
                const paymentsArr = calculatePayments(item.dateGranted, item.loanInterval, item.loanTerm, item.amountGranted, item.interestRate, item.payments, item.advanceInterest);
                const lastDueDate = dates[dates.length - 1].date;

                const loanDetails = {
                    items: [
                        { key: 'fullName', value: fullNameWithLink },
                        { key: 'loanProcessingID', value: loansNumWithLink },
                        { key: 'dateGranted', value: format(item.dateGranted, 'MM/dd/yyyy') },
                        { key: 'dueDate', value: lastDueDate },
                        { key: 'totalBalance', value: <ToPeso amount={paymentsArr.remainingBalance} /> }
                    ]
                };

                if (!acc[officer]) {
                    acc[officer] = [];
                }
                acc[officer].push(loanDetails);
                return acc;
            }, {});

            setOfficerLoans(prevState => ({
                ...prevState,
                [officer]: loansData[officer] || []
            }));

            const paymentHeaders = [
                { value: "Full Name" },
                { value: "Loan No." },
                { value: "Date Granted" },
                { value: "Due Date" },
                { value: "Total Balance" }
            ];

            setPaymentHeaders(paymentHeaders);
            setShowSpinner(false);
        } catch (error) {
            console.error('Error fetching payments:', error);
            setShowSpinner(false);
        }
    };

    useEffect(() => {
        if (officers) {
            officers.forEach((item) => {
                fetchLoans(endDate, item.accountOfficer);
            });
        }
    }, [endDate, officers]);

    const handleGeneratePDF = async () => {
        setShowSpinner(true);
        try {
            // Fetch the list of officers
            const officersResponse = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all/officers`, {
                params: {
                    endDate: endDate || format(new Date(), 'yyyy-MM-dd')
                }
            });
    
            const officers = officersResponse.data.rows;
    
            const doc = new jsPDF('landscape');
    
            const centerText = (text, y, fontWeight = 'normal') => {
                doc.setFont(undefined, fontWeight);
                const pageWidth = doc.internal.pageSize.getWidth();
                const textWidth = doc.getTextDimensions(text).w;
                const textX = (pageWidth - textWidth) / 2;
                doc.text(text, textX, y);
            };
    
            const formatDate = (date) => {
                const d = new Date(date);
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const year = d.getFullYear();
                return `${month}/${day}/${year}`;
            };
    
            const logoImg = await fetch(logo);
            const logoBlob = await logoImg.blob();
            const logoURL = URL.createObjectURL(logoBlob);
            

            // Adjust logo dimensions to keep width under 2 points with proper aspect ratio
            const logoWidth = 50; // Just under 2 points
            const aspectRatio = 60 / 12.30; // Calculate original aspect ratio
            const logoHeight = logoWidth / aspectRatio; // Maintain aspect ratio

            doc.addImage(logoURL, 'PNG', 10, 3, logoWidth, logoHeight);
    
            doc.setFontSize(14);
            centerText("Outstanding Balance Report - By Account Officer", 10, 'bold');
    
            if (endDate) {
                doc.setFontSize(12);
                centerText(`As of ${formatDate(endDate)}`, 19);
            }
    
            const tableColumn = paymentHeaders.map(header => header.value);
    
            let currentY = 30; // Start position for the first table
    
            for (const officer of officers) {
                const officerName = officer.accountOfficer;
    
                // Fetch the loan records for the current officer
                const loansResponse = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all/outstanding/byofficer`, {
                    params: {
                        endDate: endDate || format(new Date(), 'yyyy-MM-dd'),
                        officer: officerName
                    }
                });
    
                const loans = loansResponse.data.rows;
    
                if (loans.length > 0) {
                    // Add officer name heading
                    doc.setFontSize(12);
                    //centerText(`Account Officer: ${officerName}`, currentY);
                    currentY += 0;
    
                    const tableRows = loans.map(item => {
                        const dates = calculateDueDates(item.dateGranted, item.loanInterval, item.loanTerm, item.amountGranted, item.interestRate, item.advanceInterest);
                        const paymentsArr = calculatePayments(item.dateGranted, item.loanInterval, item.loanTerm, item.amountGranted, item.interestRate, item.payments, item.advanceInterest);
                        const lastDueDate = dates[dates.length - 1].date;
    
                        return [
                            `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}`,
                            item.id.toString().padStart(6, '0'),
                            format(item.dateGranted, 'MM/dd/yyyy'),
                            lastDueDate,
                            parseFloat(paymentsArr.remainingBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        ];
                    });

                    const aoffheader = [
                        `Account Officer: ${officerName}`,'','','','','',''
                    ];

                    tableRows.unshift(aoffheader);
    
                    doc.autoTable(tableColumn, tableRows, { 
                        startY: currentY,
                        didParseCell: function (data) {
                            const rowIndex = data.row.index;
                            if (rowIndex === 0) {
                                data.cell.styles.fontStyle = 'bold';
                            }
                        }
                     });
                    currentY = doc.lastAutoTable.finalY + 20; // Update currentY to be below the last table
                }
            }
    
            const now = new Date();
            const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
            doc.save(`Outstanding_Balance_Report_By_Account_Officer_${timestamp}.pdf`);
            setShowSpinner(false);
        } catch (error) {
            console.error('Error generating PDF:', error);
            setShowSpinner(false);
        }
    };    

    const handleClick = (e) => {
        e.preventDefault();
        handleGeneratePDF();
    };

    return (
        <>
            {showSpinner && (<LoadingSpinner />)}
            <div className="filters-container">
                <div className="date-range-controls">
                    <label htmlFor="endDate">As of: </label>
                    <input
                        type="date"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
                <Button href="#" onClick={handleClick} className="generate-pdf-button">
                    <i className="fa-solid fa-print"></i> Export
                </Button>
            </div>
            {officers && officers.map((officer, index) => {
                const loans = officerLoans[officer.accountOfficer];
                if (!loans || loans.length === 0) {
                    return null; // Skip rendering if there are no loans
                }
                return (
                    <div key={index}>
                        <h3 className="table-heading">{officer.accountOfficer}</h3>
                        <Table className="list-container" hover responsive>
                            <thead className="list-title">
                                <tr>
                                    {paymentHeaders.map((item, index) => (
                                        <th className="list-header-item" key={index}>{item.value}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loans.map((item, index) => (
                                    <tr className="list-body" key={index}>
                                        {item.items.map((subitem, subindex) => (
                                            <td className="list-body-item" key={`${index}+${subindex}`}>
                                                {subitem.value}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="page-buttons">
                                    <td colSpan="12">
                                        &nbsp;
                                    </td>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                );
            })}

        </>
    );
}

export default ReportOutstandingBalanceByAccountOfficer;
